// Legacy intent types for backward compatibility
export type LegacyKeywordIntent = 
  | 'category-anchor'
  | 'preparation-format'
  | 'attributes-ingredients'
  | 'dietary-health'
  | 'occasion'
  | 'audience'
  | 'sensory-benefit'
  | 'flavor-variant'
  | 'convenience-use-case'
  | 'tradition-brand';

// Dynamic intent type - can be any string
export type KeywordIntent = string;

// Copy origin metadata for tracking keyword copies
export interface CopyOrigin {
  viewType: 'product' | 'brand' | 'sub-brand';  // What view the copy was made in
  sourceId: string;                              // Product ID or brand/sub-brand identifier
  sourceName: string;                            // Human-readable name for UI display
  copiedAt: Date;                                // Timestamp of copy action
}

export interface KeywordWithIntent {
  text: string;
  intent: KeywordIntent;
  volume: number; // Search volume from 0 to 100,000
  // Optional retailer availability flags
  amazon?: boolean;
  target?: boolean;
  walmart?: boolean;
  kroger?: boolean;
  instacart?: boolean;
  // Other optional metadata fields
  ds_filter_passed?: boolean;
  research_type?: string;
  intent_score?: number;
  keyword_eng?: string;
  // Copy metadata
  copiedFrom?: CopyOrigin;                       // Present if this keyword was copied from another group
}

// Intent category configuration for a project
export interface IntentCategoryConfig {
  label: string;
  color: string;
}

export interface Brand {
  id: string;
  name: string;
  logo?: string;
  color: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  keywords: KeywordWithIntent[];
  selectedKeywords: string[];
  status: 'pending' | 'in-progress' | 'completed';
  notes?: string;
  ingredients?: string;
  legalClaims?: string;
  copy?: string;
  brand?: string;
  subBrand?: string;
}

export type ViewType = 'product' | 'brand' | 'sub-brand';

export interface Project {
  id: string;
  name: string;
  description: string;
  brand: Brand;
  products: Product[];
  createdAt: Date;
  updatedAt: Date;
  status: 'draft' | 'active' | 'completed';
  totalProducts: number;
  completedProducts: number;
  // Optional: dynamic intent categories for projects loaded from files
  intentCategories?: string[];
  intentCategoryConfig?: Record<string, IntentCategoryConfig>;
  // View type for grouping data
  viewType?: ViewType;
  // Original uploaded file information
  originalFile?: {
    name: string;
    size: number;
    uploadedAt: Date;
    data: string; // Base64 encoded file data
  };
  // Group-level notes: key is group identifier (product ID, brand name, or sub-brand name)
  // based on the current viewType
  groupNotes?: { [groupKey: string]: string };
}

export interface KeywordEvaluation {
  productId: string;
  suggestedKeywords: string[];
  selectedKeywords: string[];
  rejectedKeywords: string[];
  customKeywords: string[];
  evaluationNotes?: string;
}

// Legacy intent color mapping (for backward compatibility)
export const LEGACY_INTENT_COLORS: Record<LegacyKeywordIntent, string> = {
  'category-anchor': 'bg-blue-100 text-blue-800 border-blue-200',
  'preparation-format': 'bg-green-100 text-green-800 border-green-200',
  'attributes-ingredients': 'bg-purple-100 text-purple-800 border-purple-200',
  'dietary-health': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'occasion': 'bg-orange-100 text-orange-800 border-orange-200',
  'audience': 'bg-pink-100 text-pink-800 border-pink-200',
  'sensory-benefit': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'flavor-variant': 'bg-red-100 text-red-800 border-red-200',
  'convenience-use-case': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'tradition-brand': 'bg-gray-100 text-gray-800 border-gray-200',
};

// Legacy intent labels (for backward compatibility)
export const LEGACY_INTENT_LABELS: Record<LegacyKeywordIntent, string> = {
  'category-anchor': 'Category Anchor',
  'preparation-format': 'Preparation / Format',
  'attributes-ingredients': 'Attributes & Ingredients',
  'dietary-health': 'Dietary / Health Claims',
  'occasion': 'Occasion',
  'audience': 'Audience',
  'sensory-benefit': 'Sensory / Benefit',
  'flavor-variant': 'Flavor / Variant',
  'convenience-use-case': 'Convenience / Use Case',
  'tradition-brand': 'Tradition / Brand Perception',
};

// Backward compatibility exports
export const INTENT_COLORS = LEGACY_INTENT_COLORS as Record<string, string>;
export const INTENT_LABELS = LEGACY_INTENT_LABELS as Record<string, string>;

// Volume sorting options
export type VolumeSortOption = 'volume-high' | 'volume-low' | 'alphabetical' | 'by-intent';

export const VOLUME_SORT_LABELS: Record<VolumeSortOption, string> = {
  'volume-high': 'Volume: High to Low',
  'volume-low': 'Volume: Low to High',
  'alphabetical': 'Alphabetical',
  'by-intent': 'By Intent'
};

// Deduplication types
export type DeduplicationStrategy = 'volume' | 'intent-score' | 'first';
export type DeduplicationScope = 'within-groups' | 'across-groups';

export interface DeduplicatedKeywordInstance {
  keyword: string;
  productId: string;
  productName: string;
  groupName: string; // Brand, Sub-Brand, or Product Name based on view
  volume: number;
  intentScore?: number;
  intent: string;
  isKept: boolean;
}

export interface DeduplicationState {
  isActive: boolean;
  strategy: DeduplicationStrategy;
  scope: DeduplicationScope;
  viewType: ViewType;
  timestamp: Date;
  removedCount: number;
  // Backup of original keyword arrays for each product
  originalProductKeywords: { [productId: string]: KeywordWithIntent[] };
  // Details of all duplicate instances analyzed
  duplicateInstances: DeduplicatedKeywordInstance[];
}
